import type { OnboardingPayload } from '../types/onboardingIntake';
import { phoneExtensionError } from './phoneExtension';

const isBlank = (value: unknown) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '');

export function validateOnboardingRouting(
  payload: OnboardingPayload,
): string | null {
  const ordering = payload.ordering || {};
  const reservations = payload.reservations || {};
  const events = payload.events || {};
  const catering = payload.catering || {};
  const giftCards = payload.gift_cards || {};
  const callHandling = payload.call_handling || {};

  if (ordering.phone_order_handling === 'send_order_link') {
    const link = ordering.ordering_link || ordering.ordering_url;
    if (isBlank(link)) {
      return 'Ordering URL is required when Arthur texts an online ordering link.';
    }
  }

  const transfersOff = payload.call_handling?.human_transfer_enabled === false;
  if (transfersOff && ordering.phone_order_handling === 'transfer_to_store') {
    return 'Human transfers are not enabled, based on your earlier answer.';
  }

  if (ordering.delivery_handling === 'explain_third_party_delivery') {
    if (isBlank(ordering.how_to_order_delivery_message)) {
      return 'Delivery explanation is required when Arthur explains third-party delivery.';
    }
  }

  if (
    reservations.handling === 'send_link' &&
    isBlank(reservations.reservation_link)
  ) {
    return 'Reservation link is required when Arthur sends a reservation link.';
  }

  if (reservations.handling === 'transfer') {
    return 'Arthur can’t transfer booking calls. Choose another way to handle reservations.';
  }

  if (
    transfersOff &&
    reservations.cancellation_failure_handling === 'transfer'
  ) {
    return 'Human transfers are not enabled, based on your earlier answer.';
  }

  if (events.handling === 'text_link' && isBlank(events.event_link)) {
    return 'Event link is required when Arthur texts an events link.';
  }

  if (transfersOff && events.handling === 'transfer') {
    return 'Human transfers are not enabled, based on your earlier answer.';
  }

  if (
    events.handling === 'transfer' &&
    isBlank(events.event_forwarding_number)
  ) {
    return 'Event forwarding number is required when Arthur transfers event calls.';
  }

  if (transfersOff && catering.handling === 'transfer') {
    return 'Human transfers are not enabled, based on your earlier answer.';
  }

  if (
    catering.handling === 'transfer' &&
    isBlank(catering.catering_forwarding_number)
  ) {
    return 'Catering forwarding number is required when Arthur transfers catering calls.';
  }

  if (
    catering.handling === 'provide_contact' &&
    isBlank(catering.catering_link)
  ) {
    return 'Catering link is required when Arthur texts a catering link.';
  }

  if (giftCards.handling === 'send_link' && isBlank(giftCards.gift_card_link)) {
    return 'Gift card link is required when Arthur sends a gift card link.';
  }

  const extensionError =
    phoneExtensionError(callHandling.transfer_phone_number_ext) ||
    phoneExtensionError(ordering.order_requests_phone_number_ext) ||
    phoneExtensionError(events.event_forwarding_number_ext) ||
    phoneExtensionError(catering.catering_forwarding_number_ext);
  if (extensionError) return extensionError;

  return null;
}
